import { checkHttpStatus, parseJSON, objectToQueryString, expiredToken, okResponse, ok, errorResponse } from '@utils/response';
import { Token, UserCode, Company, EmployeeCode, EmployeeId, SessionGuid, Worksite, IsEmployeeMode, IsSupervisorMode } from '@users';
import * as StatusCode from '@scripts/StatusCode';
import * as storage from '@utils/storage'
import { sessionLogout, loginUserSuccess, loginUserSupervisorSuccess } from '@actions/login';
import { IsEnglish } from '@utils/language';
import { encryption } from '@utils/encryption';
import jwt from 'jwt-decode';
import * as AccessTypeMode from '@scripts/AccessTypeMode';
import * as keyMenu from '@scripts/KeyMenu';

export function isAuthenticated(redirect = true) {
    const token = Token();

    if (token !== null) {

        const url = 'authentication/isAuthenticated';

        const param = {};

        param.userCode = UserCode();
        param.token = token;

        return fetch(url, {
            method: 'post',
            credentials: 'include',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(param)
        })
            .then(checkHttpStatus)
            .then(parseJSON)
            .then(response => {
                try {
                    if (response.status === StatusCode.STATUS_UNAUTHORIZED) {
                        if (redirect) {
                            logout(true);
                        }

                        return false;
                    }

                    if (response.status == StatusCode.STATUS_INTERNALSERVERERROR) {
                        logout(true);
                    }

                    return true;
                } catch (e) {
                    errorResponse(response);

                    return false;
                }
            })
            .catch(error => {
                errorResponse(error);

                return false
            })
    }
    else {
        return new Promise((resolve) => {
            logout(true);

            return false;
        });
    }
}

// Value can be a company or a worksite
export function generateToken(company, worksite, accessType = AccessTypeMode.General) {
    const conn = storage.getConnectionString().value;

    const param = {};

    param.userCode = UserCode();
    param.sessionGuid = SessionGuid();
    param.conn = conn;
    param.token = Token();
    param.company = encryption(company);
    param.accessTypeCrypt = encryption(accessType);

    if (worksite != "") {
        param.worksite = encryption(worksite);
    }

    return fetch('authentication/generateTokenStep2', {
        method: 'post',
        credentials: 'include',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(param)
    })
        .then(checkHttpStatus)
        .then(expiredToken)
        .then(parseJSON)
        .then(response => {
            try {
                if (response == "undefined") {
                    errorResponse(response);

                    return false;
                }

                const userData = jwt(response.token);

                if (userData.AccessType == AccessTypeMode.Employee) {
                    const dataCompany = response.companies[0];

                    loginUserSuccess(response.token, response.isTermsValid,
                        IsEnglish() ? response.termsEn : response.termsFr,
                        IsEnglish() ? response.legalInfoEn : response.legalInfoFr,
                        response.showDateStatementTerms,
                        response.showDateStatementLegalInfo,
                        dataCompany.scheduleReturnDate,
                        dataCompany.useGeolocation,
                        dataCompany.useGeolocationZone,
                        dataCompany.creditCardSupport,
                        dataCompany.punchByTime,
                        dataCompany.colorCalendar,
                        dataCompany.useWorkInstructions,
                        dataCompany.isDisplayMedicalData,
                        dataCompany.isDisplayDistributionSlip,
                        dataCompany.isDisplayPreferenceCommunication,
                        dataCompany.isCCQ,
                        dataCompany.isPartialRequestHoursUsed,
                        dataCompany.taxationProvince,
                        dataCompany.isMandatoryPhoneCellPerso,
                        dataCompany.isMandatoryEmailPerso,
                        dataCompany.isMandatorySMSPerso,
                        dataCompany.communicationPreference,
                        response.userName,
                        dataCompany.isDisplayPayAdvanceVacation,
                        dataCompany.isUsePartialRequest,
                        dataCompany.isDisplayPayablesHours,
                        dataCompany.isUseMaterialResourceTimesheet,
                        response.notificationPublicKey,
                        response.isNotificationActivate,
                        dataCompany.isDisplayFlagFullTime,
                        dataCompany.isProfileConsultationMode,
                        dataCompany.isDisplayBankOthers,
                        dataCompany.isDisplayBankVacation,
                        dataCompany.useServiceProvider,
                        dataCompany.useGeolocationMandatory,
                        response.worksites,
                        response.isOnCallFlag,
                        dataCompany.isUseCCQStatus,
                        dataCompany.isUseCCQTask,
                        dataCompany.isDisplayChatMenu,
                        dataCompany.isDisplayExpenseAccountClassif,
                        dataCompany.useAttendanceConfirmation,
                        dataCompany.isDisplaySeniorityDate,
                        dataCompany.isDisplayInactiveDate,
                        dataCompany.isDisplayHiringDate,
                        dataCompany.isDisplayFirstDate,
                        dataCompany.isDisplayLastDate,
                        dataCompany.isDisplayEmergencyContacts,
                        dataCompany.isDisplayCCQClassifications,
                        dataCompany.isDisplayLastDevices,
                        dataCompany.isDisplayBankBalances,
                        dataCompany.canEmployeeChangePhoto,
                        dataCompany.displayBankingInformation,
                        dataCompany.useFilePunch,
                        dataCompany.useDailyReport,
                        dataCompany.useEventReport,
                        false
                    );
                }

                if (userData.AccessType == AccessTypeMode.Supervisor) { //TODO : manage isDisplayChatMenu to activate / deactivate chat Menu for Supervisor AccessTypeMode
                    loginUserSupervisorSuccess(
                        response.token,
                        response.isTermsValid,
                        IsEnglish() ? response.termsEn : response.termsFr,
                        IsEnglish() ? response.legalInfoEn : response.legalInfoFr,
                        response.showDateStatementTerms,
                        response.showDateStatementLegalInfo,
                        response.userName,
                        response.notificationPublicKey,
                        response.isNotificationActivate,
                        response.worksites,
                        false
                    );
                }

                return response;
            } catch (e) {
                errorResponse(response);

                return false;
            }
        })
        .catch(error => {
            errorResponse(error);

            return false
        })
}

export function renewToken() {
    return fetch('authentication/renewToken', {
        method: 'post',
        credentials: 'include',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            userCode: UserCode(),
            employeeCode: EmployeeCode(),
            employeeId: EmployeeId(),
            company: Company(),
            sessionGuid: SessionGuid(),
            worksite: Worksite(),
            token: Token()
        })
    })
        .then(checkHttpStatus)
        .then(expiredToken)
        .then(parseJSON)
        .then(response => {
            try {
                if (response == "undefined") {
                    alert('undefined');
                }

                return response;
            } catch (e) {
                errorResponse(response);

                return false;
            }
        })
        .catch(error => {
            errorResponse(error);

            return false
        })
}

export function getHashClient(userCode, conn, isPrefixDB) {
    let url = 'authentication/getClientHashKey?' + objectToQueryString({ userCode, conn, isPrefixDB });

    return fetch(url, {
        method: 'get',
        credentials: 'include',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        }
    })
        .then(checkHttpStatus)
        .then(parseJSON)
        .then(response => {
            try {
                if (response.status == 0) {
                    storeClientHashKey(response.key)
                    storeMinLengthPassword(response.minPasswordLength)
                    storeIsPasswordComplex(response.isComplexPassword)

                    return ok();
                }

                return errorResponse(response);
            } catch (e) {
                return errorResponse(response);
            }
        })
        .catch(error => {
            return errorResponse(error);
        })
}

export function getInitialConfig(conn) {
    let url = 'authentication/getInitialConfig?' + objectToQueryString({ conn: conn });

    return fetch(url, {
        method: 'get',
        credentials: 'include',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        }
    })
        .then(checkHttpStatus)
        .then(parseJSON)
        .then(response => {
            try {
                return okResponse(response);
            } catch (e) {
                return errorResponse(response);
            }
        })
        .catch(error => {
            return errorResponse(error);
        })
}


export function logout(expire = false) {
    sessionLogout();
    if (expire) {
        // Ignore HOME menu
        if (storage.getSelectedMenu() == keyMenu.MENU_HOME) {
            storage.saveRedirectMenu(storage.getSelectedMenu());
        }

        window.location.href = 'login/1';
    }
    else {
        window.location.href = 'login';
    }
}

export async function logoutSSO(instance) {
    instance.logoutPopup().then(() => {
        logout();
    });
}

function storeClientHashKey(key) {
    storage.saveClientHashKey(key);
}

function storeMinLengthPassword(value) {
    storage.saveMinLengthPassword(value);
}

function storeIsPasswordComplex(value) {
    storage.saveIsPasswordComplex(value);
}